import { TripType, ErrorType, EntryPoint, LocationHotelsSuppliers, FieldType } from '../models';
import { CookieUtils } from './CookieUtils';
import { DateUtils } from './DateUtils';
import { queryString, setParams } from './URLUtils';
import { getRoundedAmount } from './CurrencyUtils';
import { sendTrackingEvent, sendTrackClickEvent, sendTrackComponentEvent } from './OdigeoInteractions';
import { Config } from './Config';

export const TrackingUtils = {

    trackEvent: ({ category = TrackingUtils.CATEGORY.HOME, action, label, page }) => {
        sendTrackingEvent({ category, action, label, options: { nonInteraction: true, page } });
    },

    trackClickEvent: (trackingObject) => {
        sendTrackClickEvent(trackingObject);
    },

    trackComponentDisplayed: (trackingObject) => {
        sendTrackComponentEvent(trackingObject);
    },

    fromPageContext: ({ tracking }) => ({ action, label }) =>
        TrackingUtils.trackEvent({ ...tracking, action, label }),

    async addCrossDomainTracking(src) {
        if (/_ga=/.test(src)) {
            return Promise.resolve(src);
        }
        return setParams(src, TrackingUtils.getUtmParams());
    },

    getUtmParams: () => {
        const mktTrackToUtm = {
            utmcsr: 'utm_source',
            utmcmd: 'utm_medium',
            utmccn: 'utm_campaign',
            utmcct: 'utm_content',
            utmctr: 'utm_term',
            utmgclid: 'gclid',
        };
        const cookie = CookieUtils.readCookie('mktTrack') || '';

        if (!cookie) {
            return {};
        }

        const mktTrack = queryString(cookie, '|');
        return Object.keys(mktTrack).reduce(
            (acc, key) => ({ ...acc, [mktTrackToUtm[key]]: mktTrack[key] }),
            {},
        );
    },

    ATTEMPT_TIMEOUT: 200,
    MAX_ATTEMPTS: 30,

    PAGE: {
        HOME: '/home/',
        FLIGHTS: '/flights/home/',
        TRAINS: '/trains/home/',
        DP: '/dynpack/home/',
        HOTELS: '/hotels/home/',
    },

    CATEGORY: {
        HOME: 'home',
        FLIGHTS: 'flights_tab',
        TRAINS: 'trains_tab',
        DP: 'dynpack_tab',
        INTERNAL_OFFER: 'Internal Offer',
        HOTELS: 'hotels_tab',
    },

    ACTION: {
        FLIGHTS: 'searcher_flights',
        DP: 'searcher_dynpack',
        HOTELS: 'searcher_hotels',
        TRAINS: 'searcher_trains',
        PRODUCT_SELECTOR: 'product_selector',
        CAROUSEL: 'carousel',
        CLICK: 'click',
        HEADER: 'header',
        MYBOOKINGS: 'my_bookings_widget',
        FOOTER: 'footer',
        SEARCH_EXTENSION: 'search-extension',
        SEARCH_BY_HOUR: 'search_by_hour_widget',
    },

    LABEL: {
        addSegment: ({ tripType, segments }) =>
            tripType === TripType.MULTIPLE
                ? 'multiple_destinations_add_leg'
                : `one_way_add_flight_${segments.length + 1}`,

        cabinClass: ({ cabinClass }) => `cabin_class_${cabinClass.toLowerCase()}`,

        cabinClassSelect: (cabinClass) => `select_cabin_class_${cabinClass.toLowerCase()}`,

        deleteSegment: ({ tripType, index }) =>
            tripType === TripType.MULTIPLE
                ? 'multiple_destinations_remove_leg'
                : `one_way_remove_flight_${index + 1}`,

        residentDiscountArrow: (open) => `resident_discount_msg_${open ? 'open' : 'close'}`,

        error: (error, tripType) => {
            const errorTypes = {
                [ErrorType.DATE]: 'date_departure',
                [ErrorType.ARRIVAL]: 'location_arrival',
                [ErrorType.DEPARTURE]: 'location_departure',
                [ErrorType.ROOM]: 'age_children',
            };
            if (
                tripType === TripType.ROUND_TRIP &&
                error.type === ErrorType.DATE &&
                error.index === 1
            ) {
                return 'date_arrival_leg_1_error';
            }
            return `${errorTypes[error.type]}_leg_${error.index + 1}_error`;
        },

        // TODO talk with analytics team to change the word search_dynpack/search_flights and unify it with the rest
        errorEvent: ({ activeProduct }) => {
            const product =
                activeProduct === EntryPoint.HOTELS
                    ? 'hotels'
                    : activeProduct === EntryPoint.DP
                        ? 'dynpack'
                        : 'flights';
            return `search_${product}_FE_error`;
        },

        errorSearchAsyncEvent: () => {
            return 'search_async_timeout_error';
        },

        numberPax: ({ pax }) => `number_pax_${pax.adults}_${pax.kids}_${pax.infants}`,

        numberPaxInfo: (info) =>
            ({
                'paxSelector.children.info': 'pax_message_max_children',
                'paxSelector.infants.info': 'pax_message_max_infants',
                'paxSelector.info': 'pax_message_max_pax',
            }[info]),

        product: ({ isHotel, dynPack }) => {
            const scenario = TrackingUtils.LABEL.userScenario();
            if (isHotel && dynPack) {
                try {
                    throw new Error(
                        'Product cannot have reactContext isHotel and dynpack with true values',
                    );
                } catch (e) {
                    return 'invalid_product';
                }
            }
            return isHotel
                ? `searcher_hotels_sce:${scenario}`
                : dynPack
                    ? 'search_dynpack'
                    : 'search_flights_1';
        },

        getSupplierTrackingValue: (supplier) => {
            return supplier === LocationHotelsSuppliers.HSA ? '' : '_WL';
        },

        getRegionTypeValue: (regionType) => {
            return regionType ? `_regionType:${regionType}` : '';
        },

        hotelPopularDestinationLabel: (position) => {
            if (!position) {
                return '';
            }
            const page = 'home';
            return `hotels_popular_dest_pos:${position}_pag:${page}`;
        },

        hotelSearchLabel: ({ rooms, adults, children }) => {
            const scenario = TrackingUtils.LABEL.userScenario();
            return `rooms:${rooms}_adu:${adults}_child:${children}_sce:${scenario}`;
        },

        hotelSearchError: ({ error }) => {
            const scenario = TrackingUtils.LABEL.userScenario();
            return {
                [ErrorType.DATE]: `date_error_sce:${scenario}`,
                [ErrorType.ARRIVAL]: `destination_error_sce:${scenario}`,
            }[error.type];
        },

        hotelsDestinationSelectPos: (position, currentSearch, supplier = '') => {
            const { name = '', value = '', displayName = '', type = '', regionType = '' } = currentSearch;
            if (position > 0) {
                const page = 'home';
                return `hotels_select_dest_pos:${position}_type:${type}${TrackingUtils.LABEL.getRegionTypeValue(regionType)}`
                    + `_dest:${(name || value || displayName)}`
                    + `${TrackingUtils.LABEL.getSupplierTrackingValue(supplier)}_pag:${page}`;
            }
        },

        hotelsCalendarErrorMaxDays: (numDaysSelected) => {
            const page = 'home';
            return `hotels_calendar_error_max${DateUtils.MAX_DAYS_PRIME_HOTEL}days_${numDaysSelected}_pag:${page}`;
        },

        hotelsDestinationTyped: (displayedWord = '', supplier = '') => {
            const page = 'home';
            return `hotels_dest_type-in${(displayedWord ? `_${displayedWord.length}` : '')}`
                + `${TrackingUtils.LABEL.getSupplierTrackingValue(supplier)}_pag:${page}`;
        },

        hotelsDestinationClose: () => {
            const page = 'home';
            return `hotels_dest_close_pag:${page}`;
        },

        fieldOpen: (activeProduct, field) => {
            const page = 'home';
            const action = activeProduct === EntryPoint.HOTELS ? 'edit' : 'click';
            return `${activeProduct}_${action}-search_open_${field}_pag:${page}`;
        },

        fieldRendered: (activeProduct, field) => {
            const scenario = TrackingUtils.LABEL.userScenario();
            const userType = scenario === 'logprime' ? 'Prime' : 'nonPrime';
            const page = 'home';
            return `${activeProduct}_${field}_onload_${userType}_pag:${page}`;
        },

        formFieldClicked: (field) => {
            let label = '';
            if (FieldType.DEPARTURE === field) {
                label = 'select_origin_click';
            } else if (FieldType.ARRIVAL === field) {
                label = 'select_destination_click';
            } else if (FieldType.PAX === field) {
                label = 'select_pax_class_click';
            }
            return label;
        },

        inputErrorNoMatches: ({ activeProduct, inputField, searchValue, supplier = '' }) => {
            const page = 'home';
            const supplierValue = TrackingUtils.LABEL.getSupplierTrackingValue(supplier);
            return `${activeProduct}_${inputField}_error_no-matches_${(searchValue ?? '')}${supplierValue}_pag:${page}`;
        },

        hotelsDestinationLoadTime: (loadTime, supplier = '') => {
            const page = 'home';
            return `hotels_dest_list-time:${loadTime}${TrackingUtils.LABEL.getSupplierTrackingValue(supplier)}_pag:${page}`;
        },

        hotelsDestinationClean: () => {
            const page = 'home';
            return `hotels_dest_clean_pag:${page}`;
        },

        hotelsDestinationTypeSearch: ({ segments = [], rooms, adults, children, supplier = '' }) => {
            const page = 'home';
            const dates = segments[0]?.date && segments[1]?.date
                ? `${DateUtils.toString(segments[0]?.date)}_${DateUtils.toString(segments[1]?.date)}`
                : '';
            const { type = '', regionType = '', name = '', value = '', displayName = '' } = segments[0].destination;
            return `hotels_search_type:${type}${TrackingUtils.LABEL.getRegionTypeValue(regionType)}`
                + `_dest:${name || value || displayName}_rooms:${rooms ?? ''}`
                + `_adu:${adults ?? ''}_child:${children ?? ''}_dates:${dates ?? ''}_apt:0`
                + `${TrackingUtils.LABEL.getSupplierTrackingValue(supplier)}_pag:${page}`;
        },

        hotelDestinationSelected: () => {
            const scenario = TrackingUtils.LABEL.userScenario();
            return `destination_selected_sce:${scenario}`;
        },

        productSelector: (product) => `${product}_clicks`,

        resident: () => 'all_pax_residents_unchecked',

        roomSelection: (rooms) =>
            rooms.reduce((label, room, index) => {
                const infants = room.infants ? room.infants : 0;
                const kids = Array.isArray(room.kids) ? room.kids.length : room.kids;
                return label + `_num:${index + 1}_adu:${room.adults}_child:${kids}_inf:${infants}`;
            }, `rooms:${rooms.length}`),

        swapLocations: ({ origin, destination, tripType }) => {
            const originLabel = origin ? 'filled' : 'empty';
            const destinationLabel = destination ? 'filled' : 'empty';
            const swapTrackingLabel = {
                [TripType.ONE_WAY]: 'one_way',
                [TripType.ROUND_TRIP]: 'return',
                [TripType.MULTIPLE]: 'multidestination',
            };
            return `swap_${swapTrackingLabel[tripType]}_${originLabel}-${destinationLabel}`;
        },

        // on search submit
        tripType: ({ tripType, segments }) => {
            const isUpsell = tripType === TripType.ONE_WAY && segments.length > 1;
            const trackingTripType = isUpsell ? TripType.MULTIPLE : tripType;
            return {
                [TripType.ONE_WAY]: 'one_way',
                [TripType.ROUND_TRIP]: 'return',
                [TripType.MULTIPLE]: `multiple_destinations${isUpsell ? '_upgrade' : ''}`,
            }[trackingTripType];
        },

        separateStay: ({ enabled }) => {
            return `allow_separate_stay_${enabled ? 'checked' : 'unchecked'}`;
        },

        roomAction: ({ action, scenario = '' }) => {
            return `${action}_room${scenario}`;
        },

        userScenario: () => {
            const loggedScenario = Config.get('isPrimeCookieActive') ? 'logprime' : 'log';
            return Config.get('isUserLoggedIn') ? loggedScenario : 'nolog';
        },

        changeRoomPax: () => {
            return 'change_room-and-pax';
        },

        maxKidsPerAdult: () => {
            return 'pax_message_max_children';
        },

        // on trip type change
        tripTypeSelect: (tripType) =>
            ({
                [TripType.ONE_WAY]: 'select_one_way',
                [TripType.ROUND_TRIP]: 'select_return',
                [TripType.MULTIPLE]: 'select_multiple_destinations',
            }[tripType]),

        loadFaresInCalendarLabel: ({ tripType, pricesAvailable, daysAvailable }) => {
            const page = 'home-fl';
            const tripTypeLabel = {
                [TripType.ONE_WAY]: 'OW',
                [TripType.ROUND_TRIP]: 'RT',
                [TripType.MULTIPLE]: 'MD',
            };
            const calendarHasFareOptions = pricesAvailable > 0 ? 1 : 0;
            const pricesAvailableButckets = Math.floor(pricesAvailable / 5);
            const ratioPricesDaysAvailable = Math.ceil((pricesAvailable / daysAvailable) * 100);
            return (
                `calendar_onload_trip:${tripTypeLabel[tripType]}_` +
                `cal:${calendarHasFareOptions}_` +
                `res:${pricesAvailable}_` +
                `b:${pricesAvailableButckets}_` +
                `mav:${daysAvailable}_` +
                `cmon:${ratioPricesDaysAvailable}_` +
                `pag:${page}`
            );
        },

        faresInCalendarLabel: ({ type, calendar, tripType, faresInfo }) => {
            const page = 'home-fl';
            const {
                cheapest,
                isCheapest,
                cheapestPriceShown,
                expensivePriceShown,
                numberOfDaysWithFares,
                selectedDayFare,
                monthlyDaysWithFares,
                minMonthSeen,
                maxMonthSeen,
            } = faresInfo;
            const tripTypeLabel = {
                [TripType.ONE_WAY]: 'OW',
                [TripType.ROUND_TRIP]: 'RT',
                [TripType.MULTIPLE]: 'MD',
            };
            const calendarHasFareOptions = numberOfDaysWithFares > 0 ? 1 : 0;
            const daySelectedHasFare = selectedDayFare !== 0 ? 1 : 0;
            let dayColorCode = 'NA';
            if (selectedDayFare) {
                if (isCheapest) {
                    dayColorCode = 'GR';
                } else {
                    dayColorCode = 'GY';
                }
            }
            let trackType = type;
            let monthsInfo = '';
            let totalNumberOfDaysWithFares = numberOfDaysWithFares;
            if ('continue_calendar' === type.substring(0, 17)) {
                trackType = 'continue_calendar';
                if (DateUtils.isValidDate(minMonthSeen) && DateUtils.isValidDate(maxMonthSeen)) {
                    const monthsVisible =
                        DateUtils.getDifferenceInMonths(minMonthSeen, maxMonthSeen) + 1;
                    let daysWithPrice = [];
                    for (let i = 0; i < monthsVisible; i++) {
                        const month = DateUtils.addMonths(minMonthSeen, i);
                        const index = DateUtils.toString(month).substring(0, 7);
                        const price = monthlyDaysWithFares[index] || 0;
                        daysWithPrice.push(price);
                    }
                    totalNumberOfDaysWithFares = daysWithPrice.reduce((a, b) => a + b, 0);
                    monthsInfo = `_m:${monthsVisible}_mres:${daysWithPrice.join('-')}`;
                }
            }
            return (
                `${trackType}_` +
                `pag:${page}_` +
                `${calendar}_` +
                `trip:${tripTypeLabel[tripType]}_` +
                `cal:${calendarHasFareOptions}-${daySelectedHasFare}_` +
                `pc:${getRoundedAmount(cheapestPriceShown)}_` +
                `pe:${getRoundedAmount(expensivePriceShown)}_` +
                `ps:${getRoundedAmount(cheapest)}_` +
                `pf:${getRoundedAmount(selectedDayFare)}_` +
                `${dayColorCode}_` +
                `res:${totalNumberOfDaysWithFares}` +
                `${monthsInfo}`
            );
        },

        offerClick: (name) => name,

        offerNext: (idx) => `next_offer_displayed_${idx + 1}`,

        offerSelect: (idx) => `select_offer_ ${idx + 1}`,

        menuIconItem: (type) => `${type.substr(0, 5)}primary_${type.substr(5)}`,

        searchExtension: () => 'hotels-home_search-extension',

        searchboxOpen: (box) => `edit_search_click_open_${box}`,

        searchboxType: (field, count) => `searchbox_type:${field}_${count}`,

        searchboxEdited: (field) => `searchbox_dest_edited:${field}`,

        searchboxClean: (field) => `searchbox_clean:${field}`,

        searchboxLoadTime: (time, field) => `searchbox_list-time:${time}_${field}`,

        searchboxSelect: ({ field, pos, dest, type }) =>
            `searchbox_select:${field}_pos:${pos}_dest:${dest}_type:${type}`,

        autocompleteExposureV1: ({ field, source }) =>
            `searchbox_autocomplete_exposure_v1:${field}_source:${source}`,

        autocompleteListV1: ({ field, source, state, queryLength }) => {
            const validStates = ['shown', 'empty', 'error'];
            if (!validStates.includes(state)) {
                return '';
            }
            return (
                `searchbox_autocomplete_list_v1:${field}_source:${source}_state:${state}` +
                `_chars:${getAutocompleteQueryLengthBucket(queryLength)}`
            );
        },

        autocompleteSelectV1: ({ field, source, position, queryLength }) => {
            const normalizedPosition = Number.isInteger(position) && position > 0
                ? position
                : 'na';
            return (
                `searchbox_autocomplete_select_v1:${field}_source:${source}` +
                `_pos:${normalizedPosition}` +
                `_chars:${getAutocompleteQueryLengthBucket(queryLength)}`
            );
        },

        geolocationFunnelV1: ({ state }) => {
            const validStates = [
                'shown',
                'clicked',
                'accepted',
                'denied',
                'position_unavailable',
                'timeout',
                'unsupported',
            ];
            return validStates.includes(state)
                ? `searchbox_geolocation_v1:orig_state:${state}`
                : '';
        },

        searchboxNoMatches: (field) => `searchbox_dest_error_no_matches:${field}`,

        searchboxClose: (field) => `searchbox_dest_close:${field}`,

        searchboxTapOutside: (field) => `searchbox_dest_tap-outside:${field}`,

        tripTypeClick: (type) => `triptype_button_click:${type}`,

        searchByHourOnLoad: (hours, minutes) =>
            `search_by_hour_widget_onload:${hours}_${minutes}`,

        searchByHourClickHour: (hours, minutes, chosenHour) =>
            `search_by_hour_widget_click_hour:${hours}_${minutes}_c:${chosenHour}`,

        searchByHourClickMinute: (hours, minutes, chosenMinute) =>
            `search_by_hour_widget_click_minute:${hours}_${minutes}_c:${chosenMinute}`,
    },
};

const getAutocompleteQueryLengthBucket = (queryLength) => {
    const normalizedLength = Number.isFinite(queryLength) && queryLength >= 0
        ? Math.floor(queryLength)
        : 0;
    return normalizedLength >= 6 ? '6plus' : normalizedLength;
};
